import { Alert, Button, Text, Textarea } from "@assetwatch/ui";
import { useState } from "react";
import { type FeatureVerdicts, featureFeedbackForPoint } from "./lib";
import type { FeedbackPoint, RailItem } from "./types";

/**
 * Right box of the feedback pane: the machine's comment and Save. Keyed by
 * machine in the parent, so switching monitoring points keeps this draft.
 * Save writes the same comment onto each point, with that point's ratings
 * from the feature list. Once saved, the box is locked (greyed out) until the
 * CME clicks Edit; saving again locks it again.
 */
export function ObservationForm({
  points,
  featureVerdicts,
  initialComment,
  canSave,
  locked,
  saving,
  saveError,
  onEdit,
  onCancel,
  onSave,
}: {
  points: RailItem[];
  featureVerdicts: FeatureVerdicts;
  initialComment: string;
  canSave: boolean;
  locked: boolean;
  saving: boolean;
  saveError: string | null;
  onEdit: () => void;
  /** Present only while editing feedback that's already saved. */
  onCancel?: () => void;
  onSave: (points: FeedbackPoint[]) => Promise<void>;
}) {
  const [comment, setComment] = useState(initialComment);
  // A point only counts as reviewed with a comment, so Save needs one too.
  const hasComment = comment.trim().length > 0;
  const saveHint = !canSave
    ? hasComment
      ? "Rate every feature to save."
      : "Rate every feature and add a comment to save."
    : hasComment
      ? null
      : "Add a comment to save.";
  const payload: FeedbackPoint[] = points.map((point) => ({
    id: point.id,
    monitoring_point_id: point.monitoring_point_id,
    monitoring_point: point.monitoring_point,
    verdict: point.verdict,
    comment: comment.trim(),
    features: featureFeedbackForPoint(point, featureVerdicts[point.id]),
  }));

  async function handleSave() {
    try {
      await onSave(payload);
    } catch {
      // The parent shows the error inline; the box stays unlocked to retry.
    }
  }

  return (
    // The button sits at the bottom of the box, whatever the box's height.
    <div className="flex flex-1 flex-col gap-(--spacing-8)">
      <Textarea
        aria-label="Comment"
        disabled={locked}
        maxLength={2000}
        onChange={(event) => setComment(event.target.value)}
        placeholder="Enter your observation, findings or next steps…"
        rows={3}
        value={comment}
      />
      {saveError ? (
        <Alert
          subtext={saveError}
          title="Couldn't save feedback"
          tone="negative"
        />
      ) : null}
      <div className="mt-auto flex items-center justify-end gap-(--spacing-12)">
        {locked ? (
          <>
            <Text size="sm" tone="subtle">
              Saved. Click Edit to change it.
            </Text>
            <Button onClick={onEdit} type="button" variant="outline">
              Edit
            </Button>
          </>
        ) : (
          <>
            {saveHint ? (
              <Text size="sm" tone="subtle">
                {saveHint}
              </Text>
            ) : null}
            {onCancel ? (
              <Button
                disabled={saving}
                onClick={() => {
                  // Back to the saved comment; the parent restores the ratings.
                  setComment(initialComment);
                  onCancel();
                }}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
            ) : null}
            <Button
              disabled={saveHint !== null}
              loading={saving}
              loadingText="Saving"
              onClick={() => {
                void handleSave();
              }}
              type="button"
            >
              Save feedback
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
